import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsSection, StatusText } from "../app/SettingsSection";
import { useSession } from "../app/session";
import { lendDevice } from "./device";
import { Button, Field, Input, Select } from "../ui";

const IDLE_CHOICES = [2, 5, 10, 30, 60];

/**
 * Settings → This device (P4, circle mode): its name; *personal* (opens straight as me, no picker) or *shared*
 * (the picker, optionally again after some minutes without use).
 */
export function DeviceSettings() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const load = useSession((s) => s.load);
  const me = session?.profile;
  const device = session?.device;
  const [name, setName] = useState(device?.name ?? "");
  const [error, setError] = useState<string | null>(null);
  if (session?.mode !== "circle" || !me || me.kind === "viewer" || device?.lent) return null;

  const patch = async (body: Record<string, unknown>) => {
    const r = await fetch("/api/internal/device", {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const code = ((await r.json().catch(() => ({}))) as { error?: string }).error ?? "generic";
      setError(t(`errors.${code}`, { defaultValue: t("error.generic") }));
      return;
    }
    setError(null);
    await load();
  };
  const mode = device?.mode ?? "shared";
  const mine = mode === "personal" && device?.ownerId === me.id;

  return (
    <SettingsSection
      id="settings-device"
      intro={t("settings.intro.device")}
      title={t("device.title")}
      testId="settings-device"
    >
      <Field label={t("device.name")}>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="w-56"
            value={name}
            placeholder={t("device.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
            data-testid="device-name"
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={!name.trim() || name === device?.name}
            onClick={() => void patch({ name: name.trim() })}
          >
            {t("people.save")}
          </Button>
        </div>
      </Field>
      <Field label={t("device.mode")} hint={mine ? t("device.personalHint") : t("device.sharedHint")}>
        <Select
          value={mode}
          onChange={(e) => void patch({ mode: e.target.value })}
          data-testid="device-mode"
          className="w-fit"
        >
          <option value="shared">{t("device.shared")}</option>
          <option value="personal">
            {mode === "personal" && !mine
              ? t("device.personalFor", { name: device?.ownerName ?? "" })
              : t("device.personalFor", { name: me.name })}
          </option>
        </Select>
      </Field>
      {mode === "shared" && (
        <Field label={t("device.idle")}>
          <Select
            value={device?.idleMinutes ?? ""}
            onChange={(e) => void patch({ idleMinutes: e.target.value ? Number(e.target.value) : null })}
            data-testid="device-idle"
            className="w-fit"
          >
            <option value="">{t("device.idleNever")}</option>
            {IDLE_CHOICES.map((m) => (
              <option key={m} value={m}>
                {t("device.idleAfter", { n: m })}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {mine && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => void lendDevice()} data-testid="device-lend">
            {t("who.lend")}
          </Button>
          <StatusText tone="quiet">{t("device.lendHint", { n: device?.lendIdleMinutes ?? 15 })}</StatusText>
        </div>
      )}
      {error && (
        <p className="text-[12.5px] text-err" role="alert">
          {error}
        </p>
      )}
    </SettingsSection>
  );
}
